import React from 'react';

import Box from '@mui/material/Box';

import DocCard from '@/components/DocCard';
import { DOC_VISIBILITY_ORDER, DocNode, DocVisibility, PublicDocId } from '@/lib/api';

// TODO(2): with the new layout system, i don't think we even need depth anymore.
// we get indentation naturally with the forked-child-connector pushing in the whole
// nested DocTree.
type Visibility = DocVisibility;

/** Returns the more restrictive visibility between a node and its parent scope. */
const computeInheritedVisibility = (
  docVisibility: Visibility,
  parentVisibility?: Visibility
): Visibility => {
  if (!parentVisibility) return docVisibility;

  return DOC_VISIBILITY_ORDER[docVisibility] >= DOC_VISIBILITY_ORDER[parentVisibility]
    ? docVisibility
    : parentVisibility;
};

type DocTreeProps = {
  node: DocNode;
  level: number;
  mainDocId: PublicDocId;
  mutate: () => void;
  parentVisibility?: Visibility;
  isInFeedbackSubtree?: boolean;
};

/** Recursively renders a thread tree while preserving inherited visibility. */
const DocTree: React.FC<DocTreeProps> = ({
  node,
  level,
  mainDocId,
  mutate,
  parentVisibility,
  isInFeedbackSubtree = false,
}) => {
  const branching = node.children?.length > 1;
  const childDepth = branching ? level + 1 : level;

  // Determine if this node or its children are in a feedback subtree
  const nodeIsInFeedbackSubtree = isInFeedbackSubtree || node.is_feedback_post;

  return (
    // TODO(2): Extract `data-doc-id` into a shared constant so producers (here)
    // and consumers (LiveContext) cannot drift.
    <Box
      sx={{ width: '100%', position: 'relative', '& > article': { mb: 2 } }}
      data-doc-id={node.id}
    >
      <DocCard
        doc={node}
        isMainDoc={node.id === mainDocId}
        isMainDocChild={node.citations.some((citation) => citation.doc === mainDocId)}
        mutate={mutate}
        context="thread"
        clickable={false}
        inheritedVisibility={computeInheritedVisibility(node.visibility, parentVisibility)}
        isInFeedbackSubtree={nodeIsInFeedbackSubtree}
      />

      {node.children.map((child, index) => (
        <React.Fragment key={child.id}>
          {!branching && (
            <Box
              sx={{
                width: '20px',
                height: '32px',
                borderLeft: 4,
                borderColor: 'divider',
                marginTop: '-16px',
                marginBottom: '-16px',
                marginLeft: '8px',
              }}
            />
          )}

          <Box sx={{ display: 'flex', width: '100%' }}>
            {branching && (
              <Box
                sx={{
                  width: '20px',
                  height: '36px',
                  flexShrink: 0,
                  borderLeft: 4,
                  borderBottom: 4,
                  borderColor: 'divider',
                  borderBottomLeftRadius: '12px',
                  marginTop: '-16px',
                  marginBottom: '-20px',
                }}
              />
            )}

            <DocTree
              node={child}
              level={childDepth}
              mainDocId={mainDocId}
              mutate={mutate}
              parentVisibility={computeInheritedVisibility(node.visibility, parentVisibility)}
              isInFeedbackSubtree={nodeIsInFeedbackSubtree}
            />
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
};

export default DocTree;
